import photos from '@/content/photos.json'
import { localizeHref } from '@/i18n/href'
import { pdfLinkProps } from '@/lib/pdf'
import { useI18n } from '@/i18n/I18nProvider'
import type { SectionOf } from './shared'

/** The derived sizes `pnpm images` wrote for a `/photo/…` cover, if it has any. */
type Variants = { webp: { w: number; src: string }[]; fallback: string }
const variants = (src: string): Variants | undefined =>
  (photos as Record<string, Variants>)[src]

/**
 * The Impact Labs grid: four cover cards, a heading above them, one action under.
 *
 * This draws an `<img>` rather than going through `SitePhoto` because the cards
 * mix two kinds of source: the reports' `/photo/…` covers, and the imported
 * WordPress uploads behind the e-book and blog cards, which are not in the photo
 * manifest at all.
 *
 * But a `/photo/…` path is the manifest **key**, not a file — `pnpm images`
 * writes only the derived widths (`…-400.webp`), so pointing an `<img>` straight
 * at `item.cover.src` 404s and every report card drew as a broken image. So look
 * the cover up first and use the width it actually has; a source the manifest
 * does not know (the imported uploads) is served as-is, which is correct for it.
 */
export default function CoversSection({ section }: { section: SectionOf<'covers'> }) {
  const { locale } = useI18n()
  return (
    <div className="text-center">
      {section.heading && (
        <h2 className="text-4xl font-bold tracking-tight sm:text-5xl">{section.heading}</h2>
      )}
      {(section.sub || section.link) && (
        <p className="mx-auto mt-6 max-w-3xl text-lg text-muted-foreground">
          {section.sub}{' '}
          {section.link && (
            <a
              href={localizeHref(section.link.href, locale)}
              {...pdfLinkProps(section.link.href)}
              className="font-medium text-primary hover:underline"
            >
              {section.link.label}
            </a>
          )}
        </p>
      )}
      <ul className="mt-12 grid grid-cols-1 gap-6 text-left sm:grid-cols-2 lg:grid-cols-4">
        {section.items.map((item) => {
          const v = item.cover ? variants(item.cover.src) : undefined
          const body = (
            <>
              {item.cover ? (
                <img
                  src={v?.fallback ?? item.cover.src}
                  srcSet={v ? v.webp.map((w) => `${w.src} ${w.w}w`).join(', ') : undefined}
                  sizes="(min-width: 1024px) 20vw, (min-width: 640px) 45vw, 90vw"
                  alt={item.cover.alt}
                  loading="lazy"
                  decoding="async"
                  className="aspect-square w-full rounded-xl bg-white object-cover"
                />
              ) : (
                <div className="aspect-square w-full rounded-xl bg-white" />
              )}
              <h3 className="mt-5 text-lg font-bold leading-snug transition-colors duration-200 group-hover:text-primary">
                {item.title}
              </h3>
              {item.note && (
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">{item.note}</p>
              )}
            </>
          )
          // The card chrome sits on the link rather than the `li`, so a card
          // that goes somewhere reacts as a whole — the same lift, shadow and
          // 200ms as the report, e-book, story and blog cards. A card without
          // an `href` keeps the chrome and stays still, which is what tells a
          // reader it is not clickable.
          const chrome = 'block h-full rounded-2xl bg-band p-4 pb-6'
          return (
            <li key={item.title} className="h-full">
              {item.href ? (
                <a
                  href={localizeHref(item.href, locale)}
                  {...pdfLinkProps(item.href)}
                  className={`group ${chrome} transition duration-200 hover:-translate-y-1 hover:shadow-[var(--shadow-md)]`}
                >
                  {body}
                </a>
              ) : (
                <div className={chrome}>{body}</div>
              )}
            </li>
          )
        })}
      </ul>
      {section.cta && (
        <a
          href={localizeHref(section.cta.href, locale)}
          {...pdfLinkProps(section.cta.href)}
          className="mt-12 inline-block rounded-full border border-primary/30 bg-primary/5 px-10 py-4 font-bold text-primary transition-colors duration-200 hover:bg-primary/10"
        >
          {section.cta.label}
        </a>
      )}
    </div>
  )
}
